import {
  getScrollRegionMeta,
  type Block,
  type Line,
  type Segment,
  type ScrollRegionMeta,
  type Theme,
} from "./termui";

const ACTION_ID_ATTR = "data-termui-action-id";

type RenderUnit =
  | {
      kind: "line";
      line: Line;
    }
  | {
      kind: "scroll";
      lines: Line[];
      meta: ScrollRegionMeta;
    };

type RenderState = {
  actions: Map<string, () => void>;
};

type ScrollRegionElement = HTMLDivElement & {
  __termuiScrollMeta?: ScrollRegionMeta;
  __termuiWheelBound?: true;
};

const renderStateByRoot = new WeakMap<HTMLElement, RenderState>();

function sameScrollMeta(a: ScrollRegionMeta, b: ScrollRegionMeta): boolean {
  return (
    a.onScroll === b.onScroll &&
    a.totalLines === b.totalLines &&
    a.maxHeight === b.maxHeight &&
    a.offset === b.offset
  );
}

function buildRenderUnits(block: Block): RenderUnit[] {
  const units: RenderUnit[] = [];
  let scrollLines: Line[] = [];
  let scrollMeta: ScrollRegionMeta | undefined;

  const flushScroll = (): void => {
    if (!scrollMeta || scrollLines.length === 0) return;
    units.push({ kind: "scroll", lines: scrollLines, meta: scrollMeta });
    scrollLines = [];
    scrollMeta = undefined;
  };

  for (const line of block) {
    const meta = getScrollRegionMeta(line);
    if (!meta) {
      flushScroll();
      units.push({ kind: "line", line });
      continue;
    }
    if (!scrollMeta || !sameScrollMeta(scrollMeta, meta)) {
      flushScroll();
      scrollMeta = meta;
    }
    scrollLines.push(line);
  }

  flushScroll();
  return units;
}

function interactiveElement(target: EventTarget | null, root: HTMLElement): HTMLElement | null {
  if (!(target instanceof Node)) return null;
  const element = target instanceof Element ? target : target.parentElement;
  if (!element) return null;
  const interactive = element.closest<HTMLElement>(`[${ACTION_ID_ATTR}]`);
  if (!interactive || !root.contains(interactive)) return null;
  return interactive;
}

function activateInteraction(state: RenderState, element: HTMLElement): void {
  const actionId = element.getAttribute(ACTION_ID_ATTR);
  if (!actionId) throw new Error("Interactive segment is missing an action id.");
  const handler = state.actions.get(actionId);
  if (!handler) throw new Error(`Missing handler for action "${actionId}".`);
  handler();
}

function ensureRenderState(target: HTMLElement): RenderState {
  const existing = renderStateByRoot.get(target);
  if (existing) return existing;

  const state: RenderState = { actions: new Map() };

  target.addEventListener("pointerdown", (event) => {
    if (event.button !== 0) return;
    const element = interactiveElement(event.target, target);
    if (!element) return;
    event.preventDefault();
    element.focus();
    activateInteraction(state, element);
  });

  target.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    const element =
      interactiveElement(event.target, target) ??
      interactiveElement(document.activeElement, target);
    if (!element) return;
    event.preventDefault();
    activateInteraction(state, element);
  });

  renderStateByRoot.set(target, state);
  return state;
}

function createOrReplaceChildDiv(
  parent: HTMLElement,
  index: number,
  className: string,
): HTMLDivElement {
  const existing = parent.children[index];
  if (existing instanceof HTMLDivElement && existing.className === className) return existing;
  const div = document.createElement("div");
  div.className = className;
  if (existing) parent.replaceChild(div, existing);
  else parent.appendChild(div);
  return div;
}

function applySegmentStyle(span: HTMLSpanElement, seg: Exclude<Segment, string>, theme: Theme): void {
  span.textContent = seg.text;
  if (seg.inverted) {
    span.style.color = seg.color ?? theme.semantic.inverseFg;
    span.style.backgroundColor = seg.bg ?? theme.semantic.inverseBg;
  } else {
    span.style.color = seg.color ?? "";
    span.style.backgroundColor = seg.bg ?? "";
  }
  span.style.fontWeight = seg.bold ? "bold" : "";
  span.style.opacity = seg.dim ? "0.6" : "";
  span.style.animation = seg.blink ? "bk 1s step-end infinite" : "";
  span.style.userSelect = seg.noSelect ? "none" : "";
}

function clearSegmentStyle(span: HTMLSpanElement, text: string): void {
  span.textContent = text;
  span.style.color = "";
  span.style.backgroundColor = "";
  span.style.fontWeight = "";
  span.style.opacity = "";
  span.style.animation = "";
  span.style.userSelect = "";
}

function patchSegment(
  span: HTMLSpanElement,
  seg: Segment,
  theme: Theme,
  actions: Map<string, () => void>,
  fallbackId: string,
): void {
  if (typeof seg === "string") {
    clearSegmentStyle(span, seg);
    span.classList.remove("termui-clickable");
    span.removeAttribute("role");
    span.removeAttribute("tabindex");
    span.removeAttribute(ACTION_ID_ATTR);
    return;
  }

  applySegmentStyle(span, seg, theme);

  if (!seg.onClick) {
    span.classList.remove("termui-clickable");
    span.removeAttribute("role");
    span.removeAttribute("tabindex");
    span.removeAttribute(ACTION_ID_ATTR);
    return;
  }

  const actionId = seg.interactionId ?? fallbackId;
  if (actions.has(actionId)) {
    throw new Error(`Duplicate interaction id "${actionId}" in rendered block.`);
  }
  actions.set(actionId, seg.onClick);
  span.classList.add("termui-clickable");
  span.setAttribute("role", "button");
  span.setAttribute("tabindex", "0");
  span.setAttribute(ACTION_ID_ATTR, actionId);
}

function patchLine(
  lineDiv: HTMLDivElement,
  line: Line,
  theme: Theme,
  actions: Map<string, () => void>,
  lineKey: string,
): void {
  for (let segmentIndex = 0; segmentIndex < line.length; segmentIndex++) {
    const seg = line[segmentIndex]!;
    const current = lineDiv.children[segmentIndex];
    let span: HTMLSpanElement;
    if (current instanceof HTMLSpanElement) {
      span = current;
    } else {
      const next = document.createElement("span");
      if (current) lineDiv.replaceChild(next, current);
      else lineDiv.appendChild(next);
      span = next;
    }
    patchSegment(span, seg, theme, actions, `${lineKey}:s${segmentIndex}`);
  }

  while (lineDiv.children.length > line.length) {
    lineDiv.lastElementChild?.remove();
  }
}

function bindScrollWheel(region: ScrollRegionElement): void {
  if (region.__termuiWheelBound) return;
  region.addEventListener(
    "wheel",
    (event) => {
      const meta = region.__termuiScrollMeta;
      if (!meta) throw new Error("Scroll region received wheel input without metadata.");
      event.preventDefault();
      const step = event.deltaY > 0 ? 1 : -1;
      const maxOffset = Math.max(0, meta.totalLines - meta.maxHeight);
      const next = Math.min(maxOffset, Math.max(0, meta.offset + step));
      if (next !== meta.offset) meta.onScroll(next);
    },
    { passive: false },
  );
  region.__termuiWheelBound = true;
}

function patchScrollRegion(
  region: ScrollRegionElement,
  lines: Line[],
  meta: ScrollRegionMeta,
  theme: Theme,
  actions: Map<string, () => void>,
  regionKey: string,
): void {
  region.__termuiScrollMeta = meta;
  bindScrollWheel(region);

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex]!;
    const lineDiv = createOrReplaceChildDiv(region, lineIndex, "termui-line");
    patchLine(lineDiv, line, theme, actions, `${regionKey}:l${lineIndex}`);
  }

  while (region.children.length > lines.length) {
    region.lastElementChild?.remove();
  }
}

function patchRoot(
  target: HTMLElement,
  units: RenderUnit[],
  theme: Theme,
  actions: Map<string, () => void>,
): void {
  // Count existing non-media children for reconciliation
  const existingNonMedia: Element[] = [];
  for (const child of Array.from(target.children)) {
    if (child.classList.contains("termui-media")) continue;
    existingNonMedia.push(child);
  }

  for (let unitIndex = 0; unitIndex < units.length; unitIndex++) {
    const unit = units[unitIndex]!;
    if (unit.kind === "line") {
      const existing = existingNonMedia[unitIndex];
      let lineDiv: HTMLDivElement;
      if (existing instanceof HTMLDivElement && existing.className === "termui-line") {
        lineDiv = existing;
      } else {
        lineDiv = document.createElement("div");
        lineDiv.className = "termui-line";
        if (existing) target.replaceChild(lineDiv, existing);
        else target.insertBefore(lineDiv, target.querySelector(".termui-media"));
      }
      patchLine(lineDiv, unit.line, theme, actions, `u${unitIndex}:l0`);
      continue;
    }

    const existing = existingNonMedia[unitIndex];
    let region: ScrollRegionElement;
    if (existing instanceof HTMLDivElement && existing.className === "termui-scroll-region") {
      region = existing as ScrollRegionElement;
    } else {
      region = document.createElement("div") as ScrollRegionElement;
      region.className = "termui-scroll-region";
      if (existing) target.replaceChild(region, existing);
      else target.insertBefore(region, target.querySelector(".termui-media"));
    }
    patchScrollRegion(region, unit.lines, unit.meta, theme, actions, `u${unitIndex}`);
  }

  // Remove excess non-media children
  const nonMediaChildren = Array.from(target.children).filter(
    (c) => !c.classList.contains("termui-media"),
  );
  while (nonMediaChildren.length > units.length) {
    nonMediaChildren.pop()!.remove();
  }
}

// ---------------------------------------------------------------------------
// Media overlay rendering
// ---------------------------------------------------------------------------

function renderMediaOverlays(target: HTMLElement, block: Block): void {
  // Remove old overlays
  for (const old of Array.from(target.querySelectorAll(".termui-media"))) {
    old.remove();
  }

  for (let lineIndex = 0; lineIndex < block.length; lineIndex++) {
    const line = block[lineIndex]!;
    let colOffset = 0;
    for (const seg of line) {
      if (typeof seg === "string") {
        colOffset += seg.length;
        continue;
      }
      if (seg.media) {
        const el = document.createElement(seg.media.tag);
        if (seg.media.src) el.setAttribute("src", seg.media.src);
        if (seg.media.alt && seg.media.tag === "img") el.setAttribute("alt", seg.media.alt);
        if (seg.media.attrs) {
          for (const [k, v] of Object.entries(seg.media.attrs)) el.setAttribute(k, v);
        }
        el.className = "termui-media";
        el.style.position = "absolute";
        el.style.top = `calc(${lineIndex} * var(--cell-h))`;
        el.style.left = `${colOffset}ch`;
        el.style.width = `${seg.media.widthCells}ch`;
        el.style.height = `calc(${seg.media.heightLines} * var(--cell-h))`;
        if (seg.media.tag === "img" || seg.media.tag === "video") {
          (el as HTMLImageElement).style.objectFit = "cover";
        }
        el.style.display = "block";
        target.appendChild(el);
      }
      colOffset += seg.text.length;
    }
  }
}

export function renderTermBlock(target: HTMLElement, block: Block, theme: Theme): void {
  const state = ensureRenderState(target);
  const actions = new Map<string, () => void>();
  const units = buildRenderUnits(block);
  patchRoot(target, units, theme, actions);
  renderMediaOverlays(target, block);
  state.actions = actions;
}
